import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircle2, Pencil, UserX, RotateCcw, XCircle } from 'lucide-react';
import {
  Modal, Button, Person, TimeInput24, VisitLocationMap,
} from '../../../components';
import { homeAgencyService } from '../../../services/agencyService';
import {
  BookingPill, Field, inputClass, FieldLabel, errorMessage, longDay, timeRange, clockOf, hoursOf, money,
  stampOf, localInputOf,
} from '../../agency/shared';

/**
 * One agency booking at this home — its attendance and what the manager can
 * do next. Opened from Agencies (Bookings / Hours) and from the duty roster's
 * Agency group, so both always offer the same actions:
 *
 *   upcoming            Cancel booking (only before any clocking)
 *   due / missed        Record the hours they worked · Did not attend
 *   in progress         Record the clock-out
 *   completed           Approve hours · Edit times
 *   approved            Reopen for correction (reason required)
 *
 * Hours are the worker's own app clocking; every manager change is logged
 * with who, when and why (shifts.hours_change_log), exactly like the rota's
 * own Review flow.
 */

/** 'YYYY-MM-DDTHH:MM' edited as a date + a 24-hour time (no am/pm). */
const DateTime24 = ({ value, onChange, label }) => {
  const [date = '', time = ''] = String(value || '').split('T');
  return (
    <div className="flex gap-2">
      <input type="date" aria-label={`${label} date`} className={`${inputClass} flex-1 min-w-0`} value={date} onChange={(e) => onChange(`${e.target.value}T${time || '00:00'}`)} />
      <TimeInput24 label={label} step={1} value={time} onChange={(t) => onChange(`${date}T${t}`)} className="!w-[112px] shrink-0" />
    </div>
  );
};

const TimesForm = ({ booking, onDone, onCancel, needReason }) => {
  const a = booking.actual;
  const [clockIn, setClockIn] = useState(localInputOf(a?.clock_in) || `${booking.shift_date}T${booking.start_time}`);
  const [clockOut, setClockOut] = useState(localInputOf(a?.clock_out) || `${booking.end_date}T${booking.end_time}`);
  const [breakMinutes, setBreakMinutes] = useState(String(a?.break_minutes ?? 0));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (needReason && !reason.trim()) { setError('Say why the times are being changed.'); return; }
    setBusy(true); setError('');
    try {
      const res = await homeAgencyService.recordHours(booking.id, {
        clock_in: clockIn, clock_out: clockOut, break_minutes: Number(breakMinutes) || 0, reason,
      });
      toast.success(res.message);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><FieldLabel required>Clock in</FieldLabel><DateTime24 label="Clock in" value={clockIn} onChange={setClockIn} /></div>
        <div><FieldLabel required>Clock out</FieldLabel><DateTime24 label="Clock out" value={clockOut} onChange={setClockOut} /></div>
        <div><FieldLabel>Unpaid break (min)</FieldLabel><input type="number" min="0" className={inputClass} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} /></div>
      </div>
      <div>
        <FieldLabel required={needReason}>Reason</FieldLabel>
        <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={needReason ? 'Why are the clocked times changing?' : 'Optional'} />
      </div>
      {error && <p className="text-[12.5px] text-rose-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Back</Button>
        <Button onClick={save} loading={busy}>Save and approve</Button>
      </div>
    </div>
  );
};

const AttendanceModal = ({ booking, onClose, onChanged }) => {
  const [mode, setMode] = useState(null); // 'times' | 'no_show' | 'reopen' | 'cancel'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(null); setText(''); }, [booking]);
  if (!booking) return null;

  const a = booking.actual;
  const done = (res) => { toast.success(res.message); onChanged(); onClose(); };
  const run = async (fn) => {
    setBusy(true);
    try { done(await fn()); } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const late = a?.clock_in && booking.start_time
    ? Math.round((new Date(a.clock_in) - new Date(`${booking.shift_date}T${booking.start_time}:00`)) / 60000) : null;

  const footer = (() => {
    if (mode) return null;
    const s = booking.state;
    const buttons = [];
    if (s === 'upcoming') {
      buttons.push(<Button key="c" variant="danger-soft" leftIcon={<XCircle className="w-4 h-4" />} onClick={() => setMode('cancel')}>Cancel booking</Button>);
    }
    if (s === 'due' || s === 'missed') {
      buttons.push(<Button key="n" variant="danger-soft" leftIcon={<UserX className="w-4 h-4" />} onClick={() => setMode('no_show')}>Did not attend</Button>);
      buttons.push(<Button key="t" leftIcon={<Pencil className="w-4 h-4" />} onClick={() => setMode('times')}>Record hours</Button>);
    }
    if (s === 'in_progress') {
      buttons.push(<Button key="t" leftIcon={<Pencil className="w-4 h-4" />} onClick={() => setMode('times')}>Record clock-out</Button>);
    }
    if (s === 'completed' && booking.hours === 'to_approve') {
      buttons.push(<Button key="e" variant="secondary" leftIcon={<Pencil className="w-4 h-4" />} onClick={() => setMode('times')}>Edit times</Button>);
      buttons.push(<Button key="a" leftIcon={<CheckCircle2 className="w-4 h-4" />} loading={busy} onClick={() => run(() => homeAgencyService.approveHours(booking.id))}>Approve hours</Button>);
    }
    if (s === 'completed' && booking.hours === 'approved') {
      buttons.push(<Button key="r" variant="secondary" leftIcon={<RotateCcw className="w-4 h-4" />} onClick={() => setMode('reopen')}>Reopen for correction</Button>);
    }
    return buttons.length ? <div className="flex flex-wrap justify-end gap-2">{buttons}</div> : null;
  })();

  return (
    <Modal isOpen onClose={busy ? undefined : onClose} title="Agency shift" maxWidth="max-w-xl" footer={footer}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <Person name={booking.worker?.name} sub={[booking.worker?.user_id, booking.agency?.name].filter(Boolean).join(' · ')} image={booking.worker?.avatar_url} />
          <BookingPill booking={booking} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Date">{longDay(booking.shift_date)}</Field>
          <Field label="Booked">{timeRange(booking)}</Field>
          {booking.unit && <Field label="Unit">{booking.unit}</Field>}
          <Field label="Clock in">
            {a?.clock_in ? (
              <span>
                {clockOf(a.clock_in)}
                {late != null && Math.abs(late) > 5 && <span className={late > 0 ? 'text-amber-600' : 'text-[#64748B]'}> ({late > 0 ? `${late} min late` : `${-late} min early`})</span>}
              </span>
            ) : 'Not clocked in'}
          </Field>
          <Field label="Clock out">{a?.clock_out ? clockOf(a.clock_out) : a?.clock_in ? 'Still on shift' : '—'}</Field>
          <Field label="Worked">{a ? `${hoursOf(a.worked_minutes)}${a.break_minutes ? ` (${a.break_minutes} min break)` : ''}` : '—'}</Field>
          <Field label="Hourly charge">{money(booking.hourly_rate)}</Field>
          <Field label="Charge">{money(booking.charge)}</Field>
          <Field label="Hours">{booking.hours === 'approved' ? `Approved ${stampOf(a?.hours_approved_at)}` : booking.hours === 'to_approve' ? 'Waiting for approval' : '—'}</Field>
          {booking.reason && <div className="col-span-full"><Field label="Note">{booking.reason}</Field></div>}
        </div>

        {/* Where they clocked in and out — saved by the app with each QR scan,
            the same map as the rota's Shift Attendance window. */}
        {a?.clock_in && a.location && <VisitLocationMap subject="shift" location={a.location} />}

        {mode === 'times' && (
          <TimesForm booking={booking} needReason={Boolean(a?.clock_in)} onCancel={() => setMode(null)} onDone={() => { onChanged(); onClose(); }} />
        )}
        {['no_show', 'reopen', 'cancel'].includes(mode) && (
          <div className="flex flex-col gap-3 rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-4">
            <p className="text-[13px] text-[#475569] dark:text-gray-300">
              {mode === 'no_show' && 'Records that they did not work this shift. No hours are recorded and the agency is told.'}
              {mode === 'reopen' && 'The approved hours go back to review so you can correct them. The agency sees the change.'}
              {mode === 'cancel' && 'The place opens again and the agency and worker are told.'}
            </p>
            <div>
              <FieldLabel required={mode !== 'no_show'}>{mode === 'no_show' ? 'Note' : 'Reason'}</FieldLabel>
              <input className={inputClass} value={text} onChange={(e) => setText(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setMode(null)} disabled={busy}>Back</Button>
              <Button
                variant={mode === 'reopen' ? 'primary' : 'danger'}
                loading={busy}
                disabled={mode !== 'no_show' && !text.trim()}
                onClick={() => run(() => (mode === 'no_show'
                  ? homeAgencyService.noShow(booking.id, text)
                  : mode === 'reopen'
                    ? homeAgencyService.reopenHours(booking.id, text)
                    : homeAgencyService.cancelBooking(booking.id, text)))}
              >
                {mode === 'no_show' ? 'Record did not attend' : mode === 'reopen' ? 'Reopen hours' : 'Cancel booking'}
              </Button>
            </div>
          </div>
        )}

        {a?.hours_change_log?.length > 0 && (
          <div>
            <p className="text-[12px] font-semibold text-[#334155] dark:text-gray-300 mb-2">Hours history</p>
            <ul className="flex flex-col gap-1.5">
              {a.hours_change_log.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                  {h.from ? `${clockOf(h.from.start)}–${clockOf(h.from.end)} → ` : ''}{clockOf(h.to?.start)}–{clockOf(h.to?.end)}
                  {h.by_name ? ` · ${h.by_name}` : ''} · {stampOf(h.at)}{h.note ? ` — ${h.note}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AttendanceModal;
